import React, { useState } from 'react';
import { Plus, Trash2, Upload, Download, Save, X } from 'lucide-react';
import { VocabularyWord, Language, DifficultyLevel } from '../../types';

interface VocabularyEditorProps {
  language: Language;
  onAddWords: (words: VocabularyWord[]) => void;
  onDeleteWord: (wordId: string) => void;
  customWords: VocabularyWord[];
}

export function VocabularyEditor({
  language,
  onAddWords,
  onDeleteWord,
  customWords,
}: VocabularyEditorProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWord, setNewWord] = useState('');
  const [newLevel, setNewLevel] = useState<DifficultyLevel>(1);
  const [newCategory, setNewCategory] = useState('');
  const [filterLevel, setFilterLevel] = useState<DifficultyLevel | 'all'>('all');

  const handleAddWord = () => {
    if (!newWord.trim()) return;

    const word: VocabularyWord = {
      id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      word: newWord.trim().toLowerCase(),
      language,
      level: newLevel,
      category: newCategory.trim() || 'custom',
    };

    onAddWords([word]);
    setNewWord('');
    setNewCategory('');
    setShowAddForm(false);
  };

  const handleCSVUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const csv = event.target?.result as string;
            const lines = csv.split('\n').filter((line) => line.trim());
            const words: VocabularyWord[] = [];

            // Skip header row
            for (let i = 1; i < lines.length; i++) {
              const [word, levelStr, category] = lines[i].split(',').map((s) => s.trim());
              if (word) {
                const level = parseInt(levelStr) || 1;
                if (level >= 1 && level <= 5) {
                  words.push({
                    id: `custom-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`,
                    word: word.toLowerCase(),
                    language,
                    level: level as DifficultyLevel,
                    category: category || 'custom',
                  });
                }
              }
            }

            if (words.length > 0) {
              onAddWords(words);
              alert(`Successfully imported ${words.length} words!`);
            } else {
              alert('No valid words found in CSV file.');
            }
          } catch (err) {
            alert('Error reading CSV file. Please check the format.');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleCSVDownload = () => {
    const csv = [
      'word,level,category',
      ...customWords.map((w) => `${w.word},${w.level},${w.category || 'custom'}`),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vocabulary-${language}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = () => {
    const template = [
      'word,level,category',
      'example,1,animals',
      'test,2,colors',
      'sample,3,numbers',
    ].join('\n');

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vocabulary-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredWords = customWords
    .filter((w) => w.language === language)
    .filter((w) => filterLevel === 'all' || w.level === filterLevel)
    .sort((a, b) => a.word.localeCompare(b.word));

  const wordsByLevel = filteredWords.reduce((acc, word) => {
    if (!acc[word.level]) acc[word.level] = 0;
    acc[word.level]++;
    return acc;
  }, {} as Record<number, number>);

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-6 text-white">
        <h3 className="text-2xl font-bold mb-4">Vocabulary Editor</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2 bg-white text-purple-600 rounded-xl hover:bg-purple-50 transition-colors font-semibold"
          >
            <Plus size={20} />
            Add Word
          </button>
          <button
            onClick={handleCSVUpload}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors font-semibold"
          >
            <Upload size={20} />
            Import CSV
          </button>
          <button
            onClick={handleCSVDownload}
            disabled={customWords.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={20} />
            Export CSV
          </button>
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 rounded-xl transition-colors font-semibold"
          >
            <Download size={20} />
            Template
          </button>
        </div>
      </div>

      {/* Add Word Form */}
      {showAddForm && (
        <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-purple-200">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-xl font-bold text-gray-800">Add New Word</h4>
            <button
              onClick={() => setShowAddForm(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Word *
              </label>
              <input
                type="text"
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                placeholder="Enter word"
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Level *
              </label>
              <select
                value={newLevel}
                onChange={(e) => setNewLevel(parseInt(e.target.value) as DifficultyLevel)}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
              >
                <option value={1}>Level 1 - Beginner</option>
                <option value={2}>Level 2 - Easy</option>
                <option value={3}>Level 3 - Medium</option>
                <option value={4}>Level 4 - Hard</option>
                <option value={5}>Level 5 - Expert</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Category
              </label>
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="e.g., animals, colors"
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
              />
            </div>
          </div>
          <button
            onClick={handleAddWord}
            disabled={!newWord.trim()}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            <Save size={20} />
            Save Word
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid md:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map((level) => (
          <div
            key={level}
            className="bg-white rounded-xl p-4 text-center shadow-md hover:shadow-lg transition-shadow"
          >
            <div className="text-3xl font-bold text-purple-600">{wordsByLevel[level] || 0}</div>
            <div className="text-sm text-gray-600">Level {level}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="bg-white rounded-2xl p-4 shadow-md">
        <div className="flex items-center gap-4">
          <label className="text-sm font-semibold text-gray-700">Filter by Level:</label>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterLevel('all')}
              className={`px-4 py-2 rounded-xl font-semibold transition-colors ${
                filterLevel === 'all'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({filteredWords.length})
            </button>
            {[1, 2, 3, 4, 5].map((level) => (
              <button
                key={level}
                onClick={() => setFilterLevel(level as DifficultyLevel)}
                className={`px-4 py-2 rounded-xl font-semibold transition-colors ${
                  filterLevel === level
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                L{level}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Word List */}
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <h4 className="text-xl font-bold text-gray-800 mb-4">
          Custom Words ({filteredWords.length})
        </h4>
        {filteredWords.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredWords.map((word) => (
              <div
                key={word.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div>
                  <div className="font-semibold text-gray-800">{word.word}</div>
                  <div className="text-xs text-gray-500">
                    Level {word.level} • {word.category}
                  </div>
                </div>
                <button
                  onClick={() => onDeleteWord(word.id)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <div className="text-6xl mb-4">📝</div>
            <p>No custom words yet. Add some to get started!</p>
            <p className="text-sm mt-2">
              Click "Add Word" or "Import CSV" to add vocabulary
            </p>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
        <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
          <span className="text-2xl">💡</span>
          How to Use CSV Import
        </h4>
        <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
          <li>Download the template CSV file using the "Template" button</li>
          <li>
            Open it in Excel, Google Sheets, or any text editor
          </li>
          <li>Add your words (one per line): word,level,category</li>
          <li>Level must be 1-5, category is optional</li>
          <li>Save as CSV and upload using "Import CSV" button</li>
        </ol>
        <p className="text-xs text-blue-600 mt-3">
          Example: <code className="bg-blue-100 px-2 py-1 rounded">cat,1,animals</code>
        </p>
      </div>
    </div>
  );
}
